'use client';
import React, { useEffect, useRef } from 'react';
import Tippy from '@tippyjs/react';
import styled from '@benzinga/themetron';
//import 'tippy.js/dist/tippy.css';
import { TrackingManager } from '@benzinga/tracking-manager';
import { SessionContext } from '@benzinga/session-context';

export interface TabInterface {
  key: string;
  title: string;
  tooltip?: string;
  link: string;
}

export interface TabsProps {
  initialTabIndex?: number;
  onChange: (index: number) => void;
  scrollToActiveTab?: boolean;
  tabs: TabInterface[];
  disabled?: boolean;
}

const Tabs: React.FC<TabsProps> = ({ disabled, initialTabIndex = 0, onChange, scrollToActiveTab = false, tabs }) => {
  const session = React.useContext(SessionContext);
  const [activeTab, setActiveTab] = React.useState(initialTabIndex);
  const activeTabRef = useRef<HTMLButtonElement | null>(null);

  const onTabChange = (index: number) => {
    if (index === activeTab) return;

    setActiveTab(index);

    onChange(index);

    session.getManager(TrackingManager).trackWidgetEvent('view', 'calendar_widget', {
      widget_sub_type: tabs?.[activeTab]?.title,
    });
  };

  useEffect(() => {
    if (activeTabRef && scrollToActiveTab) {
      activeTabRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scrollToActiveTab]);

  const TabsList = () => {
    return (
      <>
        {tabs.map((item: TabInterface, index: number) => {
          const TabButton = ({ active }: { active?: boolean }) => {
            let className = 'top-tabs__tab';
            if (active) {
              className = className + ' top-tabs__tab--active';
            }
            return (
              <button
                className={className}
                disabled={disabled}
                id={`tab-${item.key}`}
                key={index}
                onClick={() => onTabChange(index)}
                ref={activeTabRef}
              >
                {item.title}
              </button>
            );
          };
          if (activeTab === index) {
            if (item.title && item.tooltip) {
              return (
                <Tippy content={item.tooltip} key={index} placement="top" theme="light-border">
                  <TabButton active={true} />
                </Tippy>
              ) as JSX.Element;
            }

            return (
              <button
                className="top-tabs__tab top-tabs__tab--active"
                disabled={disabled}
                key={index}
                onClick={() => onTabChange(index)}
                ref={activeTabRef}
              >
                {item.title}
              </button>
            ) as JSX.Element;
          }

          if (item.title && item.tooltip) {
            return (
              <Tippy content={item.tooltip} key={index} placement="top" theme="light-border">
                <h2 className="top-tabs__tab" id={`tab-${item.key}`} key={index} onClick={() => onTabChange(index)}>
                  {item.title}
                </h2>
              </Tippy>
            ) as JSX.Element;
          }

          return (<TabButton key={index} />) as JSX.Element;
        })}
      </>
    );
  };

  return (
    <TabsWrapper className="top-tabs-wrapper">
      <div className="top-tabs">
        <TabsList />
      </div>
    </TabsWrapper>
  );
};

export default Tabs;

const TabsWrapper = styled.div`
  position: relative;

  @media only screen and (max-width: 1000px) {
    width: 100%;
    order: 1;
  }

  @media only screen and (max-width: 800px) {
    &:before {
      content: '';
      display: block;
      width: 20px;
      height: 100%;
      position: absolute;
      right: 0;
      top: 0;
      background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0, #f2f7ff 67%);
      z-index: 2;
    }
  }

  .top-tabs {
    display: flex;
    position: relative;
    overflow: auto;
    gap: 0 8px;

    &__tab {
      background: rgba(26, 121, 255, 0.025);
      border: 2px solid rgba(26, 121, 255, 0.1);
      box-sizing: border-box;
      border-radius: 4px;
      color: ${({ theme }) => theme.colorPalette.blue500};
      padding: 8px 16px;
      font-size: 14px;
      font-weight: 600;
      font-family: Manrope, Manrope-fallback, sans-serif;
      line-height: 20px;
      cursor: pointer;
      white-space: nowrap;

      &:hover,
      &--active {
        background: ${({ theme }) => theme.colorPalette.blue500};
        color: #fff;
      }
    }

    @media only screen and (max-width: 800px) {
      &:after {
        content: '';
        display: block;
        width: 20px;
        flex-shrink: 0;
      }
    }
  }
`;
